import { t } from '@/locales';

export enum APP {
  TITLE = 'ZOLL Dispatch',
  PORTAL_TITLE = 'Mobile Care Connect for ZOLL Dispatch',
  PORTAL_TITLE_SHORT = 'ZOLL MCC',
  JWT_COOKIE_NAME = 'ZOLLOnline_Token',
  SESSION_ID_COOKIE_NAME = 'dispatch_sessionid',
}

export enum KENDO_UI {
  // @see https://github.com/telerik/kendo-intl#date-formatting
  DATE_FORMAT = 'MM/dd/y',
}

export enum SIGNALR {
  CONNECT_ENDPOINT = 'connect',
  WEBSOCKET_ENDPOINT = 'signalr_pipe',
}

export enum CLIENT_TYPE {
  DISPATCH = 'dispatch',
  PORTAL = 'dispatch_portal',
}

export enum CAD_STATE {
  UNINITIALIZED,
  HYBRID,
  STANDALONE,
  PORTAL,
}

export enum DEFAULT_VALUES {
  UNKNOWN_PATIENT_ID = '1', // Unknown patient
}

export enum FIXED_VALUES {
  EXTENSION = 'Ext.',
  FALSE = 'False',
  FACILITY_DEPARTMENT_TYPE = 'Facility Department Type',
  MAX_ABS_LAT = 90,
  MAX_ABS_LNG = 180,
  NO = 'No',
  TRUE = 'True',
  YES = 'Yes',
}

export enum USER_ROLES {
  ADMIN = 'custadmin',
  ZOLL_ADMIN = 'zolladmin',
}

export enum ZOLL_DISPATCH {
  ADMIN_BASE_URL = 'https://dispatchadmin.zollonline.com',
  BASE_URL = 'https://dispatch.zollonline.com',
}

export enum ZOLL_ONLINE {
  ADMIN_BASE_URL = 'https://enterpriseadmin.zollonline.com',
  AVL_BASE_URL = 'https://dispatchavl.zollonline.com',
  BASE_URL = 'https://zollonline.com',
  HELP_URL = 'https://help.zollonline.com/dispatch/Dispatch%20Standalone/Dispatch%20Standalone.htm',
  HYBRID_HELP_URL = 'https://help.zollonline.com/dispatch/Dispatch%20Hybrid/Hybrid.htm',
  PORTAL_HELP_URL = 'http://help.zollonline.com/MCC for dispatch/Mobile Care Connect for Dispatch.htm',
  LOGIN_PATH = '/Account/Logon',
  LOGOUT_PATH = '/Account/Logout',
  PORTAL_LOGOUT_PATH = '/portal/Account/Logout',
  DISPATCH_PRODUCT_KEY = '03831035-B55B-431C-B987-E1145DC84243',
  PORTAL_PRODUCT_KEY = 'FC596A28-4A28-4964-8FE3-3DA4C9C011D5',
  SERVICE_BOUNDARY_BASE_URL = 'https://dispatchserviceboundary.zollonline.com',
  SHARING_PATH = '/SecurityManagement/Sharing',
  STATIC_BASE_URL = 'https://static.zollonline.com',
  ZD_ATTACHMENT_BASE_URL = 'https://attachmentsservice.dev.zollonline.us',
}

export enum PULSEPOINT {
  AEDS_PATH = '/pulsepointBoundary/aeds',
  AED_IMAGE_PATH = '/pulsepointBoundary/aedimage',
}

export const SHIFT_ROW_PREFIX = 'shift_';

export enum USER_PILOT {
  TOKEN = 'NX-ac8de430',
}

export enum UNIT_STATUS_MAPPING {
  ASSIGNED = 'assigned',
  EN_ROUTE = 'enroute',
  AT_SCENE = 'atScene',
  TRANSPORTING = 'transporting',
  AT_DESTINATION = 'atDestination',
  PARTIALLY_AVAILABLE = 'partiallyAvailable',
  COMPLETE = 'complete',
  CANCEL = 'cancel',
  UNASSIGN = 'unassign',
  INACTIVE = 'inactive',
  ACTIVE = 'available',
  BUSY = 'busy',
  EN_ROUTE_TO_POST = 'enrouteToPost',
  AT_POST = 'atPost',
  PRE_ASSIGNED = 'preAssigned',
}

export enum UNIT_STATUS {
  DELETED = '-1',
  INACTIVE = '0',
  ACTIVE = '1',
  DISPATCHED = '3',
  BUSY = '5',
  EN_ROUTE_TO_POST = '7',
  PRE_ASSIGNED = '8',
  AT_POST = '9',
}

// Raw dispatch status codes
export const DISPATCH_STATUS_CODE = {
  OPEN: '0',
  ASSIGNED: '1',
  EN_ROUTE: '2',
  AT_SCENE: '3',
  TRANSPORTING: '4',
  AT_DESTINATION: '5',
  PARTIALLY_AVAILABLE: '6',
  COMPLETE: '7',
  PRE_ASSIGNED: '8',
  CANCEL: '-5',
  UNASSIGN: '-11',
  CALL_STARTED: '101',
} as const;

export const DISPATCH_STATUS = {
  OPEN: {
    CODE: DISPATCH_STATUS_CODE.OPEN,
    COLOR: 'var(--white)',
    BACKGROUND_COLOR: 'var(--white)',
    HEX: '#ffffff',
  },
  ASSIGNED: {
    CODE: DISPATCH_STATUS_CODE.ASSIGNED,
    COLOR: 'var(--mint-base)',
    BACKGROUND_COLOR: 'var(--green-lighten-4)',
    HEX: '#00b79b',
  },
  EN_ROUTE: {
    CODE: DISPATCH_STATUS_CODE.EN_ROUTE,
    COLOR: 'var(--mint-base)',
    BACKGROUND_COLOR: 'var(--green-lighten-4)',
    HEX: '#00b79b',
  },
  AT_SCENE: {
    CODE: DISPATCH_STATUS_CODE.AT_SCENE,
    COLOR: 'var(--mint-base)',
    BACKGROUND_COLOR: 'var(--green-lighten-4)',
    HEX: '#00b79b',
  },
  TRANSPORTING: {
    CODE: DISPATCH_STATUS_CODE.TRANSPORTING,
    COLOR: 'var(--red-base)',
    BACKGROUND_COLOR: 'var(--red-lighten-4)',
    HEX: '#d43352',
  },
  AT_DESTINATION: {
    CODE: DISPATCH_STATUS_CODE.AT_DESTINATION,
    COLOR: 'var(--red-base)',
    BACKGROUND_COLOR: 'var(--red-lighten-4)',
    HEX: '#d43352',
  },
  PARTIALLY_AVAILABLE: {
    CODE: DISPATCH_STATUS_CODE.PARTIALLY_AVAILABLE,
    COLOR: 'var(--mint-base)',
    BACKGROUND_COLOR: 'var(--green-lighten-4)',
    HEX: '#00b79b',
  },
  COMPLETED: {
    CODE: DISPATCH_STATUS_CODE.COMPLETE,
    COLOR: 'var(--gray-lighten-7)',
    BACKGROUND_COLOR: 'var(--gray-lighten-7)',
    HEX: '#787e86',
  },
  SHIFT_END: {
    COLOR: 'var(--orange-lightest)',
    BACKGROUND_COLOR: 'var(--orange-lightest)',
    HEX: '#FF9324', // darker for scheduler
  },
  PRE_ASSIGNED: {
    CODE: DISPATCH_STATUS_CODE.PRE_ASSIGNED,
    COLOR: 'var(--text-link, #2e6da4)',
    BACKGROUND_COLOR: 'var(--blue-lighten-5, #f0f7ff)',
    HEX: '#2e6da4',
    CALENDAR_BORDER_COLOR: 'var(--unit-status-preAssigned-calendar-border, #7eafd4)',
    CALENDAR_BACKGROUND_COLOR: 'var(--unit-status-preAssigned-calendar-bg, #f8fcff)',
  },
};

// Pseudo statuses for resources/units
export const PSEUDO_STATUS = {
  INACTIVE: '-1',
  AVAILABLE: '0',
  EN_ROUTE_TO_POST: '20',
  AT_POST: '21',
  SPECIAL_STATUS: '22',
  WEATHER_CHECK: '800',
} as const;

// Trip lifecycle statuses (trip.tripStatus)
export const TRIPS = {
  STATUS: {
    CANCELED: '-1',
    CREATED: '0',
    PRE_ASSIGNED: '6',
    WILL_CALL: '3',
    CANCELED_WILL_CALL: '-3',
    ON_HOLD: '4',
    OPEN: '5',
    ASSIGNED: '9',
    COMPLETED: '10',
  },
  DISPATCH_ACTIONS: {
    CREATE_TRIP: 'createTrip',
    CREATE_RECURRING_TRIP: 'createRecurringTrip',
    UPDATE_TRIP: 'updateTrip',
    ASSIGN_UNIT_TO_TRIP: 'assignUnitToTrip',
    PRE_ASSIGN_UNIT_TO_TRIP: 'preAssignUnitToTrip',
    CHANGE_UNIT_STATUS: 'changeUnitStatus',
    CANCEL_TRIP: 'cancelTrip',
  },
  CONTEXT: {
    PICK_UP: 'pickup',
    DROP_OFF: 'dropoff',
    DROPOFF: 'dropoff',
    PATIENT: 'patient',
    FACILITY: 'facility',
    PATIENT_MODAL: 'patientModal',
    COPY_TRIP: 'copyTrip',
    DATE_OF_BIRTH: 'dateOfBirth',
    PORTAL: 'portal',
  },
} as const;

export const TRIP_STATUS = [
  { id: DISPATCH_STATUS_CODE.CALL_STARTED, value: t('statusNames.callStarted'), label: t('statusNames.callStarted') },
  { id: DISPATCH_STATUS_CODE.OPEN, value: t('statusNames.open'), label: t('tripStatus.open') },
  { id: DISPATCH_STATUS_CODE.PRE_ASSIGNED, value: t('statusNames.preAssigned'), label: t('statusNames.preAssigned') },
  { id: DISPATCH_STATUS_CODE.ASSIGNED, value: t('statusNames.assigned'), label: t('statusNames.assigned') },
  { id: DISPATCH_STATUS_CODE.EN_ROUTE, value: t('statusNames.enroute'), label: t('statusNames.enroute') },
  { id: DISPATCH_STATUS_CODE.AT_SCENE, value: t('statusNames.atScene'), label: t('statusNames.atScene') },
  { id: DISPATCH_STATUS_CODE.TRANSPORTING, value: t('statusNames.transporting'), label: t('statusNames.transporting') },
  { id: DISPATCH_STATUS_CODE.AT_DESTINATION, value: t('statusNames.atDestination'), label: t('statusNames.atDestination') },
  { id: DISPATCH_STATUS_CODE.PARTIALLY_AVAILABLE, value: t('statusNames.partiallyAvailable'), label: t('statusNames.partiallyAvailable') },
  { id: DISPATCH_STATUS_CODE.COMPLETE, value: t('statusNames.complete'), label: t('statusNames.complete') },
];

export const AUTO_SCHEDULE_PREVIEW_EVENT_ID_PREFIX = 'auto-preview-';

// Unit statuses as sent by BE (string names) + UI metadata
export const UNITS = {
  DEFAULT_ID: 'unit_0',
  STATUS: {
    DISPATCHED: { NAME: 'dispatched', COLOR: 'var(--red-base)' },
    DISABLED: { NAME: 'disabled', COLOR: 'var(--gray-base)' },
    INACTIVE: { NAME: UNIT_STATUS_MAPPING.INACTIVE, COLOR: 'var(--gray-base)', CODE: '-1' },
    ACTIVE: { NAME: 'active', COLOR: 'var(--mint-base)' },
    AVAILABLE: { NAME: 'available', COLOR: 'var(--mint-base)' },
    AT_POST: { NAME: UNIT_STATUS_MAPPING.AT_POST, COLOR: 'var(--mint-base)' },
    BUSY: { NAME: UNIT_STATUS_MAPPING.BUSY, COLOR: 'var(--mint-base)' },
    ASSIGNED: { NAME: UNIT_STATUS_MAPPING.ASSIGNED, COLOR: 'var(--mint-base)' },
    PRE_ASSIGNED: { NAME: 'preAssigned', COLOR: 'var(--text-link, #2e6da4)' },
    WEATHER_CHECK: { NAME: 'weathercheck', COLOR: 'var(--mint-base)' },
    EN_ROUTE: { NAME: UNIT_STATUS_MAPPING.EN_ROUTE, COLOR: 'var(--mint-base)' },
    EN_ROUTE_TO_POST: { NAME: UNIT_STATUS_MAPPING.EN_ROUTE_TO_POST, COLOR: 'var(--mint-base)' },
    AT_SCENE: { NAME: UNIT_STATUS_MAPPING.AT_SCENE, COLOR: 'var(--mint-base)' },
    TRANSPORTING: { NAME: UNIT_STATUS_MAPPING.TRANSPORTING, COLOR: 'var(--red-base)' },
    AT_DESTINATION: { NAME: UNIT_STATUS_MAPPING.AT_DESTINATION, COLOR: 'var(--red-base)' },
    PARTIALLY_AVAILABLE: { NAME: UNIT_STATUS_MAPPING.PARTIALLY_AVAILABLE, COLOR: 'var(--mint-base)' },
    COMPLETED: { NAME: UNIT_STATUS_MAPPING.COMPLETE, COLOR: 'var(--mint-base)' },
    OPEN: { NAME: 'open', COLOR: 'var(--blue-lighten-1)' },
    UNKNOWN: { NAME: 'unknown', COLOR: 'var(--orange-base)' },
    SPECIAL_STATUS: { NAME: 'specialstatus', COLOR: 'var(--purple-lighten-4)' },
  },
} as const;

export const POST = {
  DEFAULT_ID: 0,
  HOME_POST: 'Home Post -',
} as const;

export enum WINDOW_MODE {
  NORMAL = 'NORMAL',
  EMERGENCY = 'EMERGENCY',
}

export enum WINDOW_STAGE {
  DEFAULT = 'DEFAULT',
  FULLSCREEN = 'FULLSCREEN',
  MINIMIZED = 'MINIMIZED',
  CUSTOM_FULLSCREEN = 'CUSTOM_FULLSCREEN',
}

export const COMMENT_LIMIT = 50;
export const GRID_CELL_CHAR_LITMIT = 12;

export const RECURRENCE_LIST = [{
  value: '1', label: 'Weekly',
}];

export const VIRTUAL_TRIP_TEXT = 'Virtual';

export const STATUS = {
  ENABLED: '1',
  DISABLED: '-1',
  INACTIVE: '0',
} as const;

export enum CREW {
  CONNECTED = 'Connected',
}

export enum CALENDER_VIEW {
  AGENDA = 'agenda',
  DAY = 'day',
  WEEK = 'week',
  TIMELINE = 'timeline',
}

export enum CALENDER_ORIENTATION {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export const UNSAVED_PATIENT_ID = '-100';

export const TRIP_OPERATION = {
  EDIT_SERIES: 'edit-series',
  EDIT_INSTANCE: 'edit-instance',
};

export enum ZOLL_PRODUCTS {
  DISPATCH = 'dispatch',
  PORTAL = 'portal',
}

export enum REQUEST_STATUS {
  PENDING = 1,
  REJECT = 2,
  CANCEL = 3,
  REJECT_ACCEPT = 4,
  CANCEL_ACCEPT = 5,
  APPROVED = 6,
}

export enum PROVIDER_STATUS {
  PENDING = 1,
  REJECT = 2,
  CANCEL = 3,
  REJECT_ACCEPT = 4,
  CANCEL_ACCEPT = 5,
  APPROVED = 6,
}

export const PORTAL_ACTIONS = [
  { id: REQUEST_STATUS.APPROVED, value: t('general.actions.accept'), label: t('general.actions.accept') },
  // { id: REQUEST_STATUS.REJECT, value: t('general.actions.negotiate'), label: t('general.actions.negotiate') },
];

export enum PENDING_TRIP_TAB {
  PENDING_ASSIGNMENT,
  INBOUND_QUEUE,
}

export enum TABLE {
  PENDING_ASSIGNMENT = 'pending_assignment',
  TRIP_SEARCH = 'trip_search',
  TRIP_NOTE = 'trip_note',
  TRIP_HISTORY = 'trip_history',
  INBOUND_QUEUE = 'inbound_queue',
  PENDING_REQUEST = 'pending_request',
  RESOURCES = 'resources',
}

export enum LAYOUT {
  SYSTEM_LAYOUT = 'system_layout',
}

export enum ZONE_TYPE {
  BILLING = 'BILLING',
  DISPATCH = 'DISPATCH',
  RESPONSE = 'RESPONSE',
}

export enum ZONE_TYPE_VALUE {
  DISPATCH,
  RESPONSE,
}

export enum GEOMETRY_TYPE {
  POLYGON = 'Polygon',
  MULTIPOLYGON = 'MultiPolygon',
}

export const PROQA_LABEL = 'ProQa';

export enum MAP_LAYER {
  TRAFFIC = 'traffic',
  ZONES = 'zones',
  POSTS = 'posts',
  AED = 'aeds',
  ZONE_LABEL = 'zoneLabel',
}

export enum FILTER_FIELD {
  T_STATUS = 'trip_status',
}

export const PARENT_SUFFIX = '-A';

/**
 * Google Maps API Status Constants
 */
export enum GOOGLE_API_STATUS {
  OK = 'OK',
  ZERO_RESULTS = 'ZERO_RESULTS',
  OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  INVALID_REQUEST = 'INVALID_REQUEST',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Google API HTTP Status Codes
 */
export enum GOOGLE_API_HTTP_STATUS {
  OK = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
}

export const EARTH_RADIUS_METERS = 6371000 as const;

export enum FILTER {
  NONE = 'none',
}

export enum META_DATA_PROPERTIES {
  FEATURES = 'Features',
  GENERATIONS = 'Generations',
  PRIORITY = 'Priority',
  CANCELLATION_REASONS = 'CancellationReasons',
  LATE_REASONS = 'LateReasons',
  MISC_TIMESTAMPS = 'MiscTimestamps',
  UNASSIGN_REASONS = 'UnassignReasons',
  VEHICLE_TYPE = 'VehicleType',
  TRIP_NOTE_TYPES = 'TripNoteTypes',
  VERSION = 'Version',
  CALL_TAKING_REQUIRED_FIELDS = 'CallTakingRequiredFields',
}

export const REVERSE_GEOCODE_DISTANCE_THRESHOLD_METERS = 30 as const;

export const ATTACHMENT_SHARING_TYPE = {
  NONE: '0',
  SHARING: '1',
  TENANT_NETWORK: '2',
};
